import { observable, action } from "mobx";
import PresentationStatuses, { PresentationStatus } from "const/PresentationStatuses";
import ISlideEvent, { SlideEventType } from "interfaces/ISlideEvent";
import { DateTime } from "luxon";
import { getStream } from "utils/streamUtils";
import streamConfig from "consts/streamConfig";
import URIs from "const/URIs";
import request from "utils/request";
import PdfUtils from "utils/PdfUtils";
import IFileInfo from "interfaces/IFileInfo";
import config from "config";

const PREPARATION_TIME = process.env.REACT_APP_ENV === 'development' ? 3 : 120;
const PRESENTATION_READY_TIME = process.env.REACT_APP_ENV === 'development' ? 1 : 5;
const PRESENTATION_TIME = process.env.REACT_APP_ENV === 'development' ? 100000 : 900;
// const recordOption = {mimeType: 'video/webm;codecs=vp9'};
const recordOption = {mimeType: 'video/webm;codecs=vp9', videoBitsPerSecond: 1000000, };
export default class Presentation {
  @observable presentationStatus: PresentationStatus = PresentationStatuses.PREPARATION;

  @observable stream: MediaStream|null = null;
  mediaRecorder: MediaRecorder|null = null;
  recordingBlobs: Blob[] = [];
  finalVideo: Blob|null = null;
  finalVideoUrl: string|null = null;
  
  @observable loading = false;

  //** preparation */
  @observable preparationCount: number = PREPARATION_TIME;
  @observable preparationTimer = -1;

  //** prsentationReady */
  @observable presentationReadyCount: number = PRESENTATION_READY_TIME;
  @observable presentationReadyTimer = -1;

  //** presentation */
  @observable presentationCount: number = PRESENTATION_TIME;
  @observable presentationTimer = -1;

  @observable currentSlide = 0;
  @observable history: ISlideEvent[] = [];
  @observable baseTime: DateTime = DateTime.local();

  //** presentaiton end */
  @observable presentationEnd = false;

  //** uploading */
  @observable uploading = false;

  @observable slides: string[] = [];
  @observable loadingPage: number = 0;
  @observable totalPage: number = 1;

  @observable initLoadingError = false;
  @observable isError = false;
  @observable ErrorMsg = '';


  //** modal visibles */
  @observable preparationModalVisible = false;
  @observable presentationModalVisible = false;
  @observable uploadModalVisible = false;
  @observable changePdfModalVisible = false;

  @action
  init = async () => {
    try {
      this.presentationStatus = PresentationStatuses.PREPARATION;
      this.preparationCount = PREPARATION_TIME;
      this.presentationReadyCount = PRESENTATION_READY_TIME;
      this.loading = true;
      this.initLoadingError = false;
      this.requestStream();
      try {
        if(this.slides.length === 0) {
          const pdfPath = await request<IFileInfo>({
            method: 'get',
            url: URIs.get_pt_pdf,
          });
          const doc = await PdfUtils.pdfUrl2Doc(`${config.host}${URIs.get_file(pdfPath.id)}`);
          this.totalPage = doc.numPages;
          for(let i = 0; i < doc.numPages; i++) {
            this.loadingPage = i+1;
            const canvas = await PdfUtils.pdfDoc2CanvasPage(doc, i);
            if(i === 0) {
              this.slides = [];
            }
            this.slides.push(canvas.toDataURL());
          }
        }
        this.loading = false;
      } catch (e) {
        this.loading = false;
        this.initLoadingError = true;
        throw e;
      }
      this.preparationModalVisible = false;
      this.presentationModalVisible = false;
      this.uploadModalVisible = false;
      this.changePdfModalVisible = false;
    } catch(e) {
      console.error(e);
      throw e;
    }
  }
  
  @action
  resetPresentationCount = () => this.presentationCount = 120;

  @action
  requestStream = async () => {
    try {
      this.stream = await getStream(streamConfig);
      this.mediaRecorder = new MediaRecorder(this.stream, recordOption);
      this.mediaRecorder.ondataavailable = (e: BlobEvent) => {
          if(e.data && e.data.size > 0)
          this.recordingBlobs.push(e.data);
      }
      this.mediaRecorder.onstop = () => {
        this.loading = false;
        this.finalVideo = new Blob(this.recordingBlobs, {
          type: 'video/webm'
        });
        this.finalVideoUrl = URL.createObjectURL(this.finalVideo);
        this.presentationEnd = true;
      }
    } catch(e) {
      console.error(e);
      throw e;
    }
  }

  @action
  startPresentationCount = () => {
    this.baseTime = DateTime.local();
    if(this.mediaRecorder) {
      this.mediaRecorder.start();
    }
    this.history.push({
      slideEventType: SlideEventType.NEXT,
      slideIndex:0,
      indexTime:0
    });
    this.presentationTimer = setInterval(()=>{
      if(this.presentationCount > 1)
        this.presentationCount--;
      else
        this.stopPresentationCount();
    }, 1000);
  }

  @action
  stopPresentationCount = () => {
    this.presentationCount = 0;
    clearInterval(this.presentationTimer);
    this.preparationTimer = -1;
    this.mediaRecorder?.stop();
    this.loading = true;
  }

  @action
  startPresentationReadyCount = () => {
    this.presentationReadyTimer = setInterval(()=>{
      if(this.presentationReadyCount > 1)
        this.presentationReadyCount--;
      else
        this.stopPresentationReadyCount();
    }, 1000);
  }

  @action
  stopPresentationReadyCount = () => {
    this.presentationReadyCount = 0;
    clearInterval(this.presentationReadyTimer);
    this.presentationReadyTimer = -1;
    this.presentationStatus = PresentationStatuses.PRESENTATION;
    this.currentSlide = 0;
    this.startPresentationCount();
  }

  //TODO: 이거 array형식으로 바꿈 좋겠음..-0-;
  @action
  startPreparationCount = () => {
    this.preparationTimer = setInterval(()=>{
      if(this.preparationCount > 1)
        this.preparationCount--;
      else {
        this.stopPreparationCount();
      }
    }, 1000);
  }

  @action
  stopPreparationCount = () => {
    this.preparationCount = 0;
    clearInterval(this.preparationTimer);
    this.preparationTimer = -1;
    this.presentationModalVisible = true;
  }
  
  @action
  addHistory = (slideIdx: number) => {
    this.history.push({
      slideEventType: SlideEventType.NEXT,
      slideIndex: slideIdx,
      indexTime: Math.abs(this.baseTime.diffNow('milliseconds').milliseconds)
    });
  }

  @action
  uploadHistory = async () => {
    try {
      await request({
        method:'post',
        url:URIs.post_pt_slide_event,
        data: {
          slideEvents: this.history
        }
      });
    } catch (e){
      throw e;
    }
  }

  @action
  uploadVideo = async () => {
    try {
      if(!this.finalVideo)
        throw new Error('촬영 내용이 없습니다.');
      const data = new FormData();
      data.append('file', this.finalVideo, `video.webm`);
      await request({
        method:'post',
        url:URIs.post_pt_video,
        data,
      });
    } catch (e){
      throw e;
    }
  }

  @action
  uploadRecords = async () => {
    this.uploading = true;
    await this.uploadHistory();
    await this.uploadVideo();
  }

  @action
  increasePtCount = async () => {
    await request({
      method: 'post',
      url: URIs.post_user_pt_count,
    });
  }
  
  @action
  updateLimitTime = async (limitTime: number = PRESENTATION_TIME) => {
    this.presentationCount = limitTime;
  }

  @action
  releaseTimers = () => {
    if(this.preparationTimer > 0) {
      clearInterval(this.preparationTimer);
      this.preparationTimer = -1;
    }
    if(this.presentationReadyTimer > 0) {
      clearInterval(this.presentationReadyTimer);
      this.presentationReadyTimer = -1;
    }
    if(this.presentationTimer > 0) {
      clearInterval(this.presentationTimer);
      this.presentationTimer = -1;
    }
  }

}